/**
 * Typed frontend face of the ONE recap addressing module, `shared/recap/recap-links.js`.
 *
 * The rules (Abstract keeps `recap_<date>` + the bare `/daily/<date>/`; every other chain is
 * namespaced under its hub) live there so backend services, the MCP server, the gates and
 * Next server components cannot drift apart. This file adds nothing but types — never
 * re-implement a path here, and never hand-build a `/daily/${date}` string anywhere else:
 * the `test-recap-url-builders.js` gate fails on any such literal outside these two files.
 */
import {
  DEFAULT_RECAP_CHAIN as RAW_DEFAULT_RECAP_CHAIN,
  chainFanout as rawChainFanout,
  recapChainOf as rawRecapChainOf,
  recapPath as rawRecapPath,
  recapOgCardPath as rawRecapOgCardPath,
  recapFallbackTitle as rawRecapFallbackTitle,
} from '@shared/recap/recap-links.js';

/** The chain a recap belongs to when nothing says otherwise (the registry default). */
export const DEFAULT_RECAP_CHAIN: string = RAW_DEFAULT_RECAP_CHAIN as string;

/**
 * How many chains can interleave in one date-ordered `ecosystem_recaps` stream. MULTIPLY every
 * recap read's `limit` by this: the chain scope runs in memory, so an unscaled window silently
 * returns a fraction of this chain's rows. Derived from the live slate - never hard-code 2.
 */
export function chainFanout(): number {
  return rawChainFanout() as number;
}

/** A recap doc's effective chain. Absent `chain` => Abstract (pre-multichain docs). */
export function recapChainOf(chain?: string | null): string {
  return rawRecapChainOf(chain ?? undefined) as string;
}

/** Canonical reader path, e.g. `/daily/2026-09-04/` or `/avax/daily/2026-09-04/`. */
export function recapHref(date: string, chain?: string | null): string {
  return rawRecapPath(date, chain ?? undefined) as string;
}

/** OG card path — `&chain=` for non-default chains only (Abstract stays byte-identical). */
export function recapOgCardPath(date: string, chain?: string | null): string {
  return rawRecapOgCardPath(date, chain ?? undefined) as string;
}

/** "<Chain> Daily Recap — <date>" for a recap doc with no title. */
export function recapFallbackTitle(date: string, chain?: string | null): string {
  return rawRecapFallbackTitle(date, chain ?? undefined) as string;
}
